// F18-R8 — Bundle management page (list, create, edit, delete).
import { useEffect, useState, useCallback } from 'react';
import { adminApi, getErrorMessage, type Bundle, type CreateBundleBody } from '../lib/api';

export default function Bundles() {
  const [bundles, setBundles] = useState<Bundle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Bundle | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminApi.listBundles();
      setBundles(data.bundles ?? []);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async (id: string) => {
    if (!confirm(`Delete bundle "${id}"? This cannot be undone.`)) return;
    try {
      await adminApi.deleteBundle(id);
      await load();
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const handleEdit = (bundle: Bundle) => {
    setEditing(bundle);
    setShowForm(true);
  };

  const handleCreate = () => {
    setEditing(null);
    setShowForm(true);
  };

  if (loading) return <div className="p-4 text-gray-400">Loading bundles...</div>;

  return (
    <div className="space-y-4">
    <div className="flex items-center justify-between">
      <h2 className="text-xl font-bold text-white">Bundles</h2>
      <button
        onClick={handleCreate}
        className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
      >
        + New Bundle
      </button>
    </div>

    {error && (
      <div className="rounded border border-red-800 bg-red-950/50 p-3 text-sm text-red-300">
        {error}
      </div>
    )}

    {showForm && (
      <BundleForm
        existing={editing}
        onDone={() => { setShowForm(false); setEditing(null); load(); }}
        onCancel={() => { setShowForm(false); setEditing(null); }}
      />
    )}

    {bundles.length === 0 && !showForm ? (
      <p className="text-gray-500">No bundles yet. Create one to get started.</p>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-gray-700 text-gray-400">
            <tr>
              <th className="px-3 py-2">ID</th>
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">IAP Product</th>
              <th className="px-3 py-2">Cosmetics</th>
              <th className="px-3 py-2">Discount</th>
              <th className="px-3 py-2">Active</th>
              <th className="px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {bundles.map((b) => (
              <tr key={b.id} className="border-b border-gray-800 hover:bg-gray-800/50">
                <td className="px-3 py-2 font-mono text-xs text-gray-300">{b.id}</td>
                <td className="px-3 py-2 text-white">{b.name}</td>
                <td className="px-3 py-2 font-mono text-xs text-gray-400">{b.iap_product}</td>
                <td className="px-3 py-2 text-gray-300">{b.cosmetic_ids?.length ?? 0} items</td>
                <td className="px-3 py-2 text-gray-300">{b.discount_percent}%</td>
                <td className="px-3 py-2">
                  <span className={b.is_active ? 'text-green-400' : 'text-gray-500'}>
                    {b.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-3 py-2 space-x-2">
                  <button
                    onClick={() => handleEdit(b)}
                    className="text-blue-400 hover:underline"
                  >Edit</button>
                  <button
                    onClick={() => handleDelete(b.id)}
                    className="text-red-400 hover:underline"
                  >Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
    </div>
  );
}

function BundleForm({
  existing,
  onDone,
  onCancel,
}: {
  existing: Bundle | null;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [id, setId] = useState(existing?.id ?? '');
  const [name, setName] = useState(existing?.name ?? '');
  const [iapProduct, setIapProduct] = useState(existing?.iap_product ?? '');
  const [cosmeticIdsText, setCosmeticIdsText] = useState((existing?.cosmetic_ids ?? []).join(', '));
  const [discountPercent, setDiscountPercent] = useState(existing?.discount_percent ?? 20);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaving(true);

    const cosmeticIds = cosmeticIdsText
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

    if (!name.trim()) {
      setError('Name is required');
      setSaving(false);
      return;
    }
    if (!iapProduct.trim()) {
      setError('IAP product ID is required');
      setSaving(false);
      return;
    }
    if (cosmeticIds.length < 2) {
      setError('A bundle must contain at least 2 cosmetics');
      setSaving(false);
      return;
    }
    if (discountPercent < 5 || discountPercent > 50) {
      setError('Discount must be between 5% and 50%');
      setSaving(false);
      return;
    }

    const body: CreateBundleBody = {
      id: id.trim(),
      name: name.trim(),
      iap_product: iapProduct.trim(),
      cosmetic_ids: cosmeticIds,
      discount_percent: discountPercent,
    };

    try {
      if (existing) {
        await adminApi.updateBundle(existing.id, body);
      } else {
        if (!body.id) {
          setError('Bundle ID is required for new bundles');
          setSaving(false);
          return;
        }
        await adminApi.createBundle(body);
      }
      onDone();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 rounded border border-gray-700 bg-gray-850 p-4">
      <h3 className="text-lg font-semibold text-white">
        {existing ? `Edit Bundle: ${existing.id}` : 'Create New Bundle'}
      </h3>

      {error && (
        <div className="rounded border border-red-800 bg-red-950/50 p-3 text-sm text-red-300">
          {error}
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <label className="block">
          <span className="text-sm text-gray-400">Bundle ID</span>
          <input
            value={id}
            onChange={(e) => setId(e.target.value)}
            disabled={!!existing}
            placeholder="e.g. summer_pack_2025"
            className="mt-1 w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white disabled:opacity-50"
          />
        </label>
        <label className="block">
          <span className="text-sm text-gray-400">Name</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Summer Pack 2025"
            className="mt-1 w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white"
          />
        </label>
        <label className="block">
          <span className="text-sm text-gray-400">IAP Product ID</span>
          <input
            value={iapProduct}
            onChange={(e) => setIapProduct(e.target.value)}
            placeholder="com.eatemall.bundle.summer2025"
            className="mt-1 w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white"
          />
        </label>
        <label className="block">
          <span className="text-sm text-gray-400">Discount %</span>
          <input
            type="number"
            value={discountPercent}
            onChange={(e) => setDiscountPercent(Number(e.target.value))}
            min={5}
            max={50}
            className="mt-1 w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white"
          />
        </label>
      </div>

      <label className="block">
        <span className="text-sm text-gray-400">Cosmetic IDs (comma-separated)</span>
        <textarea
          value={cosmeticIdsText}
          onChange={(e) => setCosmeticIdsText(e.target.value)}
          placeholder="showoff_name_style_gold, showoff_name_frame_diamond, showoff_avatar_frame_crown"
          rows={3}
          className="mt-1 w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white"
        />
      </label>

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={saving}
          className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? 'Saving...' : existing ? 'Update Bundle' : 'Create Bundle'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded border border-gray-700 px-4 py-2 text-sm text-gray-300 hover:bg-gray-800"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
